import { Controller, useForm } from "react-hook-form";
import type { ReactNode } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button2 } from "@/components/UI2/atoms/Button";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { TextField2 } from "@/components/UI2/molecules/TextField";

import { addendumSchema, type AddendumFormValues } from "./schema";

type AddendumFormProps = {
  children?: ReactNode;
  defaultValue?: string;
  isPending: boolean;
  mutate: (addendum: string) => void;
  onCancel: () => void;
};

export function AddendumForm({
  children,
  defaultValue,
  isPending,
  mutate,
  onCancel,
}: AddendumFormProps) {
  const { control, formState, handleSubmit } = useForm<AddendumFormValues>({
    defaultValues: { addendum: defaultValue ?? "" },
    mode: "onChange",
    resolver: zodResolver(addendumSchema),
  });

  return (
    <form
      className="tw:flex tw:flex-col tw:gap-6"
      onSubmit={handleSubmit(({ addendum }) => mutate(addendum))}
      {...getAutomationDataAttr("note-addendum-form")}
    >
      <Controller
        control={control}
        name="addendum"
        render={({ field, fieldState }) => (
          <TextField2
            errorMessage={fieldState.error?.message}
            isInvalid={!!fieldState.error}
            label=""
            mode="textarea"
            onChange={field.onChange}
            placeholder="Type Comment..."
            textAreaProps={{
              className: "tw:h-32",
              ...getAutomationDataAttr("note-addendum-comment"),
            }}
            value={field.value}
          />
        )}
      />

      {children}

      <div className="tw:flex tw:items-end tw:justify-end tw:gap-4">
        <Button2 onPress={onCancel} variant="secondary">
          Cancel
        </Button2>

        <Button2
          isDisabled={!formState.isValid}
          isLoading={isPending}
          labelLoading={null}
          type="submit"
          variant="primary"
          {...getAutomationDataAttr("note-addendum-save")}
        >
          Save
        </Button2>
      </div>
    </form>
  );
}
